import useGenreOptions from "../hooks/useGenreOptions";
// src/pages/EventsIndex.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Chip,
  Stack,
  Button,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  CardMedia,
  CardActionArea,
  CardActions,
  Grid,
  Container,
  Skeleton,
  Tooltip,
  useMediaQuery,
  Snackbar,
  Alert,
  Drawer,
  Autocomplete,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ShareIcon from "@mui/icons-material/Share";
import ReportIcon from "@mui/icons-material/Report";
import { eventService } from "../services/resources.service";
import DiscoveryMap from "../components/DiscoveryMap";
import { FilterButton, ViewModeToggle } from "../components/DiscoveryActions";
import { getEventProximityQuery } from "../services/eventDiscovery";
import CommittedRadiusSlider from "../components/CommittedRadiusSlider";
import { INFINITE_RADIUS, NEAR_ME_RADIUS_KM, radiusLabel } from "../services/radius";
import { formatDateTime, formatEventPrice, hasEventPrice, useLocale, useMessage } from "../i18n/locale";
import { fetchAllMapEntities } from "../services/mapEntities";
import useDebouncedSearch from "../hooks/useDebouncedSearch";
import { uniqueEvents } from "../services/eventDeduplication";
import { BANNER_IMAGE_FALLBACK, imageFallbackHandler } from "../services/profileImages";

/**
 * Supported params (example):
 *  - q, dateRange: "Today|Weekend|Month|All"
 *  - radiusKm
 *  - genres[]
 *  - price: "free|lt100|100-300|gt300|any"
 *  - accessibility[]
 *  - page, pageSize
 */
async function fetchEvents({
  q,
  dateRange,
  radiusKm,
  genres,
  price,
  accessibility,
  page,
  pageSize,
  latitude,
  longitude,
}) {
  return eventService.list({ q, dateRange, radiusKm, genres, price, accessibility, page, pageSize, latitude, longitude, upcomingOnly: true });
}

// -------------- Small UI utilities --------------
const Price = ({ event }) => {
  const locale = useLocale();
  return (
    <Typography variant="body2" sx={{ fontWeight: 700 }}>
      {formatEventPrice(event, locale)}
    </Typography>
  );
};

function EventCard({ event, onClick, onSaveCalendar, onShare, onReport }) {
  const locale = useLocale();
  return (
    <Card variant="outlined" sx={{ borderRadius: 3, overflow: "hidden", ":hover": { boxShadow: 2 } }}>
      <CardActionArea onClick={onClick}>
        <CardMedia
          component="img"
          height="160"
          image={event.poster || BANNER_IMAGE_FALLBACK}
          onError={imageFallbackHandler(BANNER_IMAGE_FALLBACK)}
          alt={`${event.title} poster`}
          loading="lazy"
        />
        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Stack spacing={0.75}>
            <Typography variant="caption" color="text.secondary">
              {formatDateTime(event.startAt, locale)}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                lineHeight: 1.25,
                overflow: "hidden",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
              }}
            >
              {event.title}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                overflow: "hidden",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
              }}
            >
              {event?.lineup?.map((a) => a.name).join(", ")} • {event?.venue?.name} ({event?.venue?.city})
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: "wrap", gap: 1 }}>
              {event.tags?.slice(0, 3).map((t) => (
                <Chip key={t} label={t} size="small" />
              ))}
              {hasEventPrice(event) && (
                <Chip
                  icon={<CalendarMonthIcon />}
                  label={<Price event={event} />}
                  size="small"
                  variant="outlined"
                />
              )}
            </Stack>
          </Stack>
        </CardContent>
      </CardActionArea>
      <CardActions sx={{ justifyContent: "space-between", px: { xs: 1.5, sm: 2 }, pb: { xs: 1.5, sm: 2 } }}>
        <Stack direction="row" spacing={1}>
          <Button size="small" startIcon={<CalendarMonthIcon />} onClick={onSaveCalendar}>
            Save
          </Button>
          <Button size="small" startIcon={<ShareIcon />} onClick={onShare}>
            Share
          </Button>
        </Stack>
        <Tooltip title="Report event">
          <IconButton onClick={onReport} aria-label="Report event">
            <ReportIcon />
          </IconButton>
        </Tooltip>
      </CardActions>
    </Card>
  );
}

// -------------- Main Page --------------
export default function EventsIndex({ onNavigate, onChangeRegion, discoveryLocation, discoveryRadiusKm, onChangeDiscoveryRadius, region = "Anywhere" }) {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));

  // Search & filters (same keys as before)
  const { inputValue: searchInput, setInputValue: setSearchInput, searchValue: q, submitSearch } = useDebouncedSearch();
  const [dateRange, setDateRange] = useState("All"); // Today | Weekend | Month | All
  const dateToday = useMessage("eventDateToday");
  const dateWeekend = useMessage("eventDateWeekend");
  const dateMonth = useMessage("eventDateMonth");
  const dateAll = useMessage("eventDateAll");
  const radiusKm = discoveryRadiusKm;
  const setRadiusKm = onChangeDiscoveryRadius;
  const [genres, setGenres] = useState(["All"]); // multi
  const [price, setPrice] = useState("any"); // free | lt100 | 100-300 | gt300 | any
  const [acc, setAcc] = useState([]); // accessibility selections
  const accOptions = ["Wheelchair", "Hearing loop", "Family-friendly"];
  const genreOptions = useGenreOptions();
  const priceOptions = [
    { key: "any", label: "Any" },
    { key: "free", label: "Free" },
    { key: "lt100", label: "< R100" },
    { key: "100-300", label: "R100–R300" },
    { key: "gt300", label: "> R300" },
  ];

  // Map/List toggle
  const [mapMode, setMapMode] = useState(false);

  // Data & pagination
  const [events, setEvents] = useState([]);
  const [mapEvents, setMapEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const pageSize = 6;

  // Snackbar
  const [snack, setSnack] = useState({ open: false, msg: "", severity: "info" });

  // NEW: Filters drawer
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    let active = true;
    fetchAllMapEntities(eventService.list, {
      q,
      dateRange,
      genres,
      price,
      accessibility: acc,
      ...getEventProximityQuery(radiusKm, discoveryLocation),
    }).then((items) => {
      if (active) setMapEvents(uniqueEvents(items));
    });
    return () => { active = false; };
  }, [q, dateRange, radiusKm, genres, price, acc, discoveryLocation]);

  // Fetch
  const runFetch = async ({ reset = false } = {}) => {
    setLoading(true);
    const nextPage = reset ? 1 : page;
    const proximity = getEventProximityQuery(radiusKm, discoveryLocation);
    const res = await fetchEvents({
      q,
      dateRange,
      genres,
      price,
      accessibility: acc,
      page: nextPage,
      pageSize,
      ...proximity,
    });
    setLoading(false);
    setHasMore(res.hasMore);
    if (reset) {
      setPage(1);
      setEvents(uniqueEvents(res.data));
    } else {
      setEvents((prev) => uniqueEvents([...prev, ...res.data]));
    }
  };

  // Initial + on filter change -> reset results
  useEffect(() => {
    runFetch({ reset: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, dateRange, radiusKm, JSON.stringify(genres), price, JSON.stringify(acc), discoveryLocation]);

  // Handlers
  const toggleAcc = (a) => {
    setAcc((prev) => {
      const s = new Set(prev);
      s.has(a) ? s.delete(a) : s.add(a);
      return Array.from(s);
    });
  };

  const handleGenresChange = (_, values) => {
    if (!values?.length) return setGenres(["All"]);
    if (values.includes("All")) return setGenres(["All"]);
    setGenres(values);
  };

  const resetFilters = () => {
    setDateRange("All");
    setGenres(["All"]);
    setRadiusKm(INFINITE_RADIUS);
    setPrice("any");
    setAcc([]);
  };

  const saveToCalendar = (evt) => {
    const start = new Date(evt.startAt).toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
    const ics = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "PRODID:Band-Joe",
      "BEGIN:VEVENT",
      `UID:${evt.id}@band-joe`,
      `DTSTAMP:${start}`,
      `DTSTART:${start}`,
      `SUMMARY:${evt.title}`,
      `DESCRIPTION:${evt.lineup?.map((a) => a.name).join(", ")} at ${evt.venue?.name}`,
      "END:VEVENT",
      "END:VCALENDAR",
    ].join("\r\n");
    const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${evt.title}.ics`;
    a.click();
    URL.revokeObjectURL(url);
    setSnack({ open: true, msg: "Event saved to calendar", severity: "success" });
  };

  const shareEvent = async (evt) => {
    const url = `${window.location.origin}/events/${evt.id}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: evt.title, url });
        setSnack({ open: true, msg: "Shared!", severity: "success" });
      } catch {
        // ignore
      }
    } else {
      await navigator.clipboard.writeText(url);
      setSnack({ open: true, msg: "Link copied to clipboard", severity: "info" });
    }
  };

  const reportEvent = () =>
    setSnack({ open: true, msg: "Thanks. Our team will review this event.", severity: "info" });

  // Derived subtitle & active filter count
  const subtitle = useMemo(() => {
    const parts = [region];
    if (dateRange !== "All") parts.push(dateRange);
    if (radiusKm !== INFINITE_RADIUS) parts.push(radiusLabel(radiusKm));
    if (genres.length && !genres.includes("All")) parts.push(genres.join(", "));
    if (price !== "any") {
      const p = priceOptions.find((p) => p.key === price)?.label;
      if (p) parts.push(p);
    }
    if (acc.length) parts.push(acc.join(", "));
    return parts.join(" • ");
  }, [region, dateRange, radiusKm, genres, price, acc]);

  const activeCount = useMemo(() => {
    let n = 0;
    if (dateRange !== "All") n++;
    if (!(genres.length === 1 && genres[0] === "All")) n++;
    if (radiusKm !== INFINITE_RADIUS) n++;
    if (price !== "any") n++;
    if (acc.length) n++;
    return n;
  }, [dateRange, genres, radiusKm, price, acc]);

  return (
    <Box sx={{ minHeight: "100dvh", bgcolor: "background.default" }}>
      {/* App Bar */}
      <AppBar position="sticky" elevation={0} color="inherit">
        <Toolbar sx={{ gap: 1 }}>
          <IconButton edge="start" aria-label="back" onClick={() => onNavigate?.("/")}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" noWrap sx={{ fontWeight: 900, letterSpacing: 0.2 }}>
            Events
          </Typography>

          <Box component="form" onSubmit={submitSearch} sx={{ flex: 1, mx: 1 }}>
            <TextField
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              size="small"
              fullWidth
              placeholder="Search events (e.g. rock tonight)"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Map/List toggle (visible ≥ sm) */}
          <ViewModeToggle
            mapMode={mapMode}
            onChange={setMapMode}
            sx={{ display: { xs: "none", sm: "inline-flex" } }}
          />
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 2 }}>
        {/* Ultra-compact filter bar */}
        <Stack spacing={1.5} sx={{ mb: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap", rowGap: 1 }}>
            <Chip
              icon={<LocationOnIcon />}
              label={region}
              variant={radiusKm === INFINITE_RADIUS ? "outlined" : "filled"}
              onClick={async () => {
                if (radiusKm !== INFINITE_RADIUS) {
                  setRadiusKm(INFINITE_RADIUS);
                  return;
                }
                if (discoveryLocation) {
                  setRadiusKm(NEAR_ME_RADIUS_KM);
                  return;
                }
                const location = await onChangeRegion?.();
                if (location) setRadiusKm(NEAR_ME_RADIUS_KM);
              }}
            />

            {/* Quick date segmented control */}
            <ToggleButtonGroup
              exclusive
              size="small"
              value={dateRange}
              onChange={(_, v) => v && setDateRange(v)}
              sx={{
                ml: 0.5,
                borderRadius: 999,
                "& .MuiToggleButton-root": { textTransform: "none", px: 1.25 },
              }}
            >
              <ToggleButton value="All">{dateAll}</ToggleButton>
              <ToggleButton value="Today">{dateToday}</ToggleButton>
              <ToggleButton value="Weekend">{dateWeekend}</ToggleButton>
              <ToggleButton value="Month">{dateMonth}</ToggleButton>
            </ToggleButtonGroup>

            {/* Filters button (badge shows active count) */}
            <FilterButton
              count={activeCount}
              onClick={() => setFiltersOpen(true)}
              sx={{ ml: "auto" }}
            />
          </Stack>

          <Typography variant="body2" color="text.secondary">
            {subtitle ? `Showing: ${subtitle}` : "Showing all events"}
          </Typography>
        </Stack>

        {/* Map */}
        {mapMode && (
          <Box sx={{ mb: 2 }}>
            <DiscoveryMap
              items={mapEvents}
              getCoordinates={(event) => event.venue?.geo}
              getLabel={(event) => event.title}
              onSelect={(event) => onNavigate?.(`/events/${event.id}`)}
              height={320}
            />
          </Box>
        )}

        {/* List */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {(loading && events.length === 0 ? Array.from({ length: 6 }) : events).map((evt, idx) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={evt?.id ?? idx}>
              {evt ? (
                <EventCard
                  event={evt}
                  onClick={() => onNavigate?.(`/events/${evt.id}`)}
                  onSaveCalendar={() => saveToCalendar(evt)}
                  onShare={() => shareEvent(evt)}
                  onReport={reportEvent}
                />
              ) : (
                <Skeleton variant="rounded" height={260} sx={{ borderRadius: 3 }} />
              )}
            </Grid>
          ))}
        </Grid>

        {/* Pagination */}
        <Stack alignItems="center" sx={{ py: 3 }}>
          {hasMore ? (
            <Button
              size="large"
              disabled={loading}
              onClick={async () => {
                setPage((p) => p + 1);
                setLoading(true);
                const res = await fetchEvents({
                  q,
                  dateRange,
                  radiusKm,
                  genres,
                  price,
                  accessibility: acc,
                  page: page + 1,
                  pageSize,
                });
                setLoading(false);
                setHasMore(res.hasMore);
                setEvents((prev) => uniqueEvents([...prev, ...res.data]));
              }}
            >
              {loading ? "Loading…" : "Load more"}
            </Button>
          ) : (
            <Typography variant="body2" color="text.secondary">
              You’ve reached the end.
            </Typography>
          )}
        </Stack>
      </Container>

      {/* Filters Drawer (bottom on mobile, right on desktop) */}
      <Drawer
        anchor={isMdUp ? "right" : "bottom"}
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        PaperProps={{
          sx: {
            width: { md: 360 },
            borderTopLeftRadius: { xs: 16, md: 0 },
            borderTopRightRadius: { xs: 16, md: 0 },
            p: 2,
            gap: 2,
          },
        }}
      >
        <Stack spacing={2}>
          <Typography variant="h6" fontWeight={800}>
            Filters
          </Typography>

          {/* Date */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Date
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={dateRange}
              onChange={(_, v) => v && setDateRange(v)}
            >
              <ToggleButton value="All">{dateAll}</ToggleButton>
              <ToggleButton value="Today">{dateToday}</ToggleButton>
              <ToggleButton value="Weekend">{dateWeekend}</ToggleButton>
              <ToggleButton value="Month">{dateMonth}</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Genres (multi-select) */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Genres
            </Typography>
            <Autocomplete
              multiple
              size="small"
              options={genreOptions}
              value={genres.includes("All") ? [] : genres}
              onChange={handleGenresChange}
              renderInput={(params) => <TextField {...params} placeholder="Select genres" />}
              renderTags={(value, getTagProps) =>
                value.slice(0, 3).map((option, index) => (
                  <Chip key={option} label={option} size="small" {...getTagProps({ index })} />
                ))
              }
            />
            <Button size="small" sx={{ mt: 0.5 }} onClick={() => setGenres(["All"])}>
              Clear genres
            </Button>
          </Box>

          {/* Radius */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Radius: {radiusLabel(radiusKm)}
            </Typography>
            <CommittedRadiusSlider value={radiusKm} onChange={setRadiusKm} />
          </Box>

          {/* Price */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Price
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={price}
              onChange={(_, v) => v && setPrice(v)}
              sx={{ flexWrap: "wrap" }}
            >
              {priceOptions.map((p) => (
                <ToggleButton key={p.key} value={p.key}>
                  {p.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          {/* Accessibility */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Accessibility
            </Typography>
            <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
              {accOptions.map((a) => (
                <Chip
                  key={a}
                  label={a}
                  size="small"
                  variant={acc.includes(a) ? "filled" : "outlined"}
                  onClick={() => toggleAcc(a)}
                />
              ))}
            </Stack>
          </Box>

          <Stack direction="row" justifyContent="space-between" gap={1} sx={{ pt: 1 }}>
            <Button onClick={resetFilters}>Reset</Button>
            <Button
              variant="contained"
              onClick={() => {
                setFiltersOpen(false);
                runFetch({ reset: true });
              }}
            >
              Apply
            </Button>
          </Stack>
        </Stack>
      </Drawer>

      {/* Snackbars */}
      <Snackbar
        open={snack.open}
        autoHideDuration={2200}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snack.severity} sx={{ width: "100%" }}>
          {snack.msg}
        </Alert>
      </Snackbar>

      {/* Bottom safe area */}
      <Box sx={{ height: 16 }} />
    </Box>
  );
}
